import { InputProps } from '@mui/material'
import * as Yup from 'yup'

import { FieldSettings, FormHelpersProviderReturn } from '../../../ana-toolbox'
import { InsuranceType } from '../../../ana-toolbox'
import { MaskedDateInput } from '../../components/masked-date-input/masked-date-input'
import { Type, hasFamilyInsuranceData, isPatientUnderaged } from '../entities/Form'
import { createFields } from '../helpers/createFields'
import { resetFields } from '../helpers/resetFields'
import { addressValidation, adultValid, dateValidation, latinValidation, maxAgeValid } from '../yup/yup'

Yup.addMethod<Yup.StringSchema>(Yup.string, 'maxAge', maxAgeValid)
Yup.addMethod<Yup.StringSchema>(Yup.string, 'birthday', dateValidation)
Yup.addMethod<Yup.StringSchema>(Yup.string, 'adult', adultValid)
Yup.addMethod<Yup.StringSchema>(Yup.string, 'address', addressValidation)
Yup.addMethod<Yup.StringSchema>(Yup.string, 'latin', latinValidation)

const genderOptions = [
  { label: 'option.female', value: 'FEMALE' },
  { label: 'option.male', value: 'MALE' },
  { label: 'option.diverse', value: 'DIVERSE' },
]

const isFilledForADifferentPerson = (values: Partial<Type>): boolean => Boolean(values.filedForDifferentPerson)

export const personalInformationFields = createFields({
  filedForDifferentPerson: {
    __type: 'toggle',
    label: 'field.anamnesis_for_another_person_label',
    name: 'filedForDifferentPerson',
    helperText: 'field.anamnesis_for_another_person_helper',
    hooks: {
      beforeChange: (value: unknown, helpers: FormHelpersProviderReturn, _event) => {
        if (typeof value !== 'boolean') return
        if (value) return

        resetFields(
          [
            'contactPersonFirstName',
            'contactPersonLastName',
            'contactPersonBirthday',
            'contactPersonCity',
            'contactPersonStreet',
            'contactPersonZipcode',
          ],
          helpers
        )
      },
    },
    visibleWhen: (values: Partial<Type>): boolean => {
      // When the patient is underaged, we need to show the contact person form.
      if (isPatientUnderaged(values)) return true

      return values.bookedForDifferentPerson !== false
    },
    validation: Yup.boolean().when('birthday', {
      is: (birthday: string) => isPatientUnderaged({ birthday }),
      then: Yup.boolean()
        .oneOf([true], 'field.filled_for_different_person')
        .required('field.filled_for_different_person'),
      otherwise: Yup.boolean(),
    }),
  },
  contactPersonFirstName: {
    __type: 'text',
    label: 'field.contact_person_first_name_label',
    name: 'contactPersonFirstName',
    isRequired: true,
    visibleWhen: isFilledForADifferentPerson,
    validation: Yup.string()
      .latin()
      .when('filedForDifferentPerson', {
        is: true,
        then: Yup.string().required('field.contact_person_first_name_error'),
        otherwise: Yup.string(),
      }),
  },
  contactPersonLastName: {
    __type: 'text',
    label: 'field.contact_person_last_name_label',
    name: 'contactPersonLastName',
    isRequired: true,
    visibleWhen: isFilledForADifferentPerson,
    validation: Yup.string()
      .latin()
      .when('filedForDifferentPerson', {
        is: true,
        then: Yup.string().required('field.contact_person_last_name_error'),
        otherwise: Yup.string(),
      }),
  },
  contactPersonBirthday: {
    __type: 'text',
    label: 'field.date_of_birth',
    name: 'contactPersonBirthday',
    isRequired: true,
    inputProps: {
      inputComponent: MaskedDateInput as InputProps['inputComponent'],
      InputLabelProps: {
        shrink: true,
      },
    },
    visibleWhen: isFilledForADifferentPerson,
    validation: Yup.string().when('filedForDifferentPerson', {
      is: true,
      then: Yup.string()
        .birthday({ empty: 'birthday_form_error', invalidDate: 'invalid_date', inPast: 'in_past' })
        .adult('legal_representative_valid_age')
        .required(),
      otherwise: Yup.string(),
    }),
  },
  contactPersonCity: {
    __type: 'text',
    label: 'field.city',
    name: 'contactPersonCity',
    isRequired: true,
    visibleWhen: isFilledForADifferentPerson,
    validation: Yup.string()
      .latin()
      .when('filedForDifferentPerson', {
        is: true,
        then: Yup.string().required('city_error'),
        otherwise: Yup.string(),
      }),
    hooks: {
      beforeChange: (_value, helpers: FormHelpersProviderReturn, _event) => {
        helpers.setFieldValue('contactAddressGoogleVerified', false)
      },
    },
  },
  contactPersonStreet: {
    __type: 'address',
    label: 'field.street_and_number',
    name: 'contactPersonStreet',
    isRequired: true,
    inputProps: {
      autoFocus: true,
    },
    visibleWhen: isFilledForADifferentPerson,
    validation: Yup.string()
      .latin()
      .when('filedForDifferentPerson', {
        is: true,
        then: Yup.string().address('invalid_address_error').required('street_and_number_error'),
        otherwise: Yup.string(),
      }),
  },
  contactPersonZipcode: {
    __type: 'text',
    label: 'field.zipCode',
    name: 'contactPersonZipcode',
    isRequired: true,
    validation: Yup.string()
      .latin()
      .when('filedForDifferentPerson', {
        is: true,
        then: Yup.string().required('zipCode_error'),
        otherwise: Yup.string(),
      }),
    hooks: {
      beforeChange: (_value, helpers: FormHelpersProviderReturn, _event) => {
        helpers.setFieldValue('contactAddressGoogleVerified', false)
      },
    },
    visibleWhen: isFilledForADifferentPerson,
  },
  contactPersonRelation: {
    __type: 'text',
    label: 'field.contact_person_relation_label',
    name: 'contactPersonRelation',
    visibleWhen: isFilledForADifferentPerson,
    validation: Yup.string().latin(),
  },
  visitReason: {
    __type: 'text',
    label: 'field.visit_reason_label',
    name: 'visitReason',
    helperText: 'field.visit_reason_helper',
    validation: Yup.string().latin(),
  },
  gender: {
    __type: 'radioGroup',
    label: 'field.gender',
    name: 'gender',
    isRequired: true,
    options: genderOptions,
    validation: Yup.string().oneOf(['MALE', 'FEMALE', 'DIVERSE']).required('gender_error'),
    hooks: {
      beforeChange: (gender, { setFieldValue }, _event) => {
        if (typeof gender === 'string' && gender === 'MALE') {
          setFieldValue('isPregnant', undefined)
          setFieldValue('pregnantMonths', undefined)
        }
      },
    },
  },
  firstName: {
    __type: 'text',
    label: 'field.first_name',
    name: 'firstName',
    isRequired: true,
    validation: Yup.string().latin().required('name_error'),
  },
  lastName: {
    __type: 'text',
    label: 'field.last_name',
    name: 'lastName',
    isRequired: true,
    validation: Yup.string().latin().required('name_error'),
  },
  birthday: {
    __type: 'text',
    label: 'field.date_of_birth',
    name: 'birthday',
    isRequired: true,
    inputProps: {
      type: 'text',
      inputComponent: MaskedDateInput as InputProps['inputComponent'],
      InputLabelProps: {
        shrink: true,
      },
    },
    validation: Yup.string()
      .birthday({ empty: 'birthday_form_error', invalidDate: 'invalid_date', inPast: 'in_past' })
      .maxAge('max_age')
      .required(),
    hooks: {
      beforeChange: (value, { setFieldValue, getFieldProps }: FormHelpersProviderReturn, _event) => {
        if (typeof value !== 'string') return

        const holderFirstName = getFieldProps<string>('holderFirstName').value
        const holderLastName = getFieldProps<string>('holderLastName').value
        const holderCity = getFieldProps<string>('holderCity').value
        const holderStreet = getFieldProps<string>('holderStreet').value
        const holderZipcode = getFieldProps<string>('holderZipcode').value
        const holderBirthday = getFieldProps<string>('holderBirthday').value
        const insuranceType = getFieldProps<string>('type').value

        if (isPatientUnderaged({ birthday: value }) && insuranceType === InsuranceType.PUBLIC) {
          setFieldValue('hasFamilyInsurance', false)
        } else if (
          !hasFamilyInsuranceData({
            holderFirstName,
            holderLastName,
            holderCity,
            holderStreet,
            holderZipcode,
            holderBirthday,
          })
        ) {
          setFieldValue('hasFamilyInsurance', undefined)
        }
      },
    },
  },
  placeOfBirth: {
    __type: 'text',
    label: 'field.place_of_birth',
    name: 'placeOfBirth',
    isRequired: true,
    validation: Yup.string().latin().required('place_of_birth_error'),
    helperText: 'field.place_of_birth_helper',
  },
  bodyWeightInKg: {
    __type: 'text',
    isRequired: true,
    label: 'field.ana_weight',
    name: 'bodyWeightInKg',
    helperText: 'field.ana_weight_helper',
    validation: Yup.number()
      .required()
      .moreThan(2)
      .lessThan(635)
      .integer()
      .transform((_, value) => {
        if (typeof value === 'string' && (value.includes('.') || value.includes(','))) {
          return null
        }

        return +value
      }),
  },
  bodyHeightInCm: {
    __type: 'text',
    isRequired: true,
    label: 'field.ana_height',
    name: 'bodyHeightInCm',
    helperText: 'field.ana_height_helper',
    validation: Yup.number()
      .required()
      .moreThan(54)
      .lessThan(300)
      .integer()
      .transform((_, value) => {
        if (typeof value === 'string' && (value.includes('.') || value.includes(','))) {
          return null
        }

        return +value
      }),
  },
  occupation: {
    __type: 'text',
    label: 'field.occupation',
    name: 'occupation',
    optional: true,
    helperText: 'field.occupation_helper',
    inputProps: {
      type: 'text',
    },
    validation: Yup.string().latin(),
  },
})

const buildSchema = <T extends string>(fields: Record<T, FieldSettings>) => {
  return Object.fromEntries(
    Object.entries<FieldSettings>(fields)
      .map(([key, settings]) => [key, settings.validation])

      .filter(([_, validation]) => Boolean(validation))
  )
}

export const personalInformationValidationSchema = Yup.object().shape(buildSchema(personalInformationFields))
